// src/utils/ocr/mergeFromConfig.ts
import * as chrono from 'chrono-node';

import {
  RESOURCE_CURATION_CONFIG,
  normalizeFormType,
} from '@constants/RESOURCE_CURATION_CONFIG';

/**
 * Generic OCR merge:
 *  - looks up config by (normalized) formType
 *  - pulls text from record._bboxMap[label]
 *  - falls back to record[field] when available
 *  - applies boolean normalizers
 *  - applies custom assembler if present
 */
export function mergeOcrFromConfig(formType: string, record: any): any {
  if (!record) return {};

  const normalizedType = normalizeFormType(formType);
  if (!(normalizedType in RESOURCE_CURATION_CONFIG)) return {};
  const cfg = RESOURCE_CURATION_CONFIG[normalizedType];
  if (!cfg.ocrMap) return {};

  const bboxMap: Record<string, any[]> = record._bboxMap ?? {};
  const flat: Record<string, any> = {};
  const structuredFields = new Set(cfg.structuredOcrFields ?? []);

  // List of date fields that need normalization
  const dateFields = new Set([
    'procedureDate', // Procedure
    'onsetDateTime', // Condition
    'visitDate', // Appointment
    'startDate', // Emergency Visits, Hospitalizations
    'endDate', //Emergency Visits, Hospitalizations
    'startingDate', // Medications
    'deceasedDate', // Family History
    'immunizationDate', // Immunizations
    'reportedDate', // Imaging and Tests
    'testDate', // Imaging and Tests, vital-sign, Lab Results, Genetic Information
    'date', //General
  ]);

  // 1) basic text extraction based on ocrMap
  for (const [targetField, labels] of Object.entries(cfg.ocrMap)) {
    for (const label of labels) {
      const entries = bboxMap[label];
      const entry = Array.isArray(entries) ? entries[0] : undefined;

      // OCR'd text wins: it's what the curator can see on the page.
      const text = getTrimmedString(entry?.text);

      // Discern's newer output can carry a field as a FHIR structure rather than
      // text (e.g. Medication.ingredient[]), which coercing to a string would
      // destroy. Gated on an explicit per-config allowlist rather than "is this
      // an object", because `record` is `{...cfg.defaults, ...flat}` and several
      // configs default a mapped field to an object: "Imaging and Tests" and
      // "Genetic Information" both use `code: { text: '' }`. Treating those as
      // OCR-provided lands an object in a string form field and short-circuits
      // the rest of the label list.
      const structured =
        !text && structuredFields.has(targetField)
          ? (objectValue(record[label]) ?? objectValue(record[targetField]))
          : undefined;
      if (structured !== undefined) {
        flat[targetField] = structured;
        break;
      }

      // Scalars are safe for every field: before this they were dropped outright,
      // since getTrimmedString only accepts strings.
      const scalar = text ? undefined : scalarValue(record[label]);
      if (scalar !== undefined) {
        flat[targetField] = scalar;
        break;
      }

      const candidate =
        text ||
        getTrimmedString(record[targetField]) ||
        getTrimmedString(record[label]) ||
        '';

      if (candidate) {
        // If this is a date field, normalize it
        if (dateFields.has(targetField)) {
          const normalizedDate = normalizeDateString(candidate);

          flat[targetField] = normalizedDate;
        } else {
          flat[targetField] = candidate;
        }
        break;
      }
    }
  }

  // 2) boolean normalizers (e.g. isDeceased)
  if (cfg.booleanNormalizers) {
    for (const [field, normalizer] of Object.entries(cfg.booleanNormalizers)) {
      const raw = flat[field] ?? '';
      flat[field] = normalizer(raw);
    }
  }

  // 3) custom assembler for complex shapes (vital-sign, Lab Results)
  if (cfg.customOcrAssembler) {
    return cfg.customOcrAssembler(flat, bboxMap);
  }

  return flat;
}

function getTrimmedString(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed;
}

/**
 * An object or non-empty array worth passing through untouched. Empty arrays are
 * skipped so a config default like `ingredients: []` can't win over a real OCR
 * value further down the label list.
 */
function objectValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.length ? value : undefined;
  if (value !== null && typeof value === 'object') return value;
  return undefined;
}

/**
 * A JSON scalar discern may send in place of OCR text. Numbers become strings
 * because every downstream mapper treats these fields as strings; booleans stay
 * as-is because the normalizers accept them.
 */
function scalarValue(value: unknown): unknown {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  if (typeof value === 'boolean') return value;
  return undefined;
}

function normalizeDateString(dateStr: string): string | null {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const cleaned = dateStr.trim();

  // Handle year-only (e.g., "2025" -> "2025")
  if (/^\d{4}$/.test(cleaned)) {
    return cleaned;
  }

  // (handles "Oct 17 2025", "October 2025", "October 17, 2025", etc.)
  const date = chrono.parseDate(cleaned);
  if (date && !isNaN(date.getTime())) {
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  }

  return null;
}
